import { useMemo } from 'react'
import type { WorldFlowHop, WorldFlowLink } from '@/analysis/worldFlowTypes'
import { useStore } from '@/store'

/**
 * Connected World pipeline flow. Data lifecycle is owned by DataController:
 *  - REST `/api/world` once on subscribe / filter / grain / time change
 *  - live `world` WS messages thereafter (no REST polling)
 */
export function useWorldFlowData(): {
  hops: WorldFlowHop[]
  links: WorldFlowLink[]
  regionSeverity: Record<string, 0 | 1 | 2 | null>
  epoch: number
  loading: boolean
  error: string | null
  fromMs: number
  toMs: number
} {
  const flow = useStore((s) => s.worldFlowResult)
  const loading = useStore((s) => s.streamsLoading)
  const error = useStore((s) => s.lastDataError)
  const lastTs = useStore((s) => s.lastStreamsUpdateTs)
  const includeInactive = useStore((s) => s.includeInactive)

  return useMemo(() => {
    const rawHops = flow?.hops ?? []
    const rawLinks = flow?.links ?? []

    // Active-only: hide zero-stream hops/links entirely.
    const hops = includeInactive
      ? rawHops
      : rawHops.filter((h) => h.totalStreams > 0)
    const hopIds = new Set(hops.map((h) => `${h.type}:${h.value}`))
    const links = includeInactive
      ? rawLinks
      : rawLinks.filter((l) => {
          if (l.totalStreams <= 0) return false
          const fromId = `${l.from.type}:${l.from.value}`
          const toId = `${l.to.type}:${l.to.value}`
          return hopIds.has(fromId) && hopIds.has(toId)
        })

    return {
      hops,
      links,
      regionSeverity: flow?.regionSeverity ?? {},
      epoch: lastTs ?? flow?.ts ?? 0,
      loading,
      error,
      fromMs: flow?.fromMs ?? 0,
      toMs: flow?.toMs ?? 0,
    }
  }, [flow, loading, error, lastTs, includeInactive])
}
